import React, { useEffect, useState } from "react";
import KpiStrip from "./KpiStrip";
import Tag from "./Tag";
import { money } from "../format";
import TablePagination from "../../../../../../../Components/Pagination/TablePagination";

const OPEN_LABEL = {
  inventory: "Open SKU",
  inbound: "View shipment",
  recovery: "View claim",
  health: "Open system health",
};

function ActionList({ title, items, urgent, onOpen, empty }) {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const list = items || [];

  useEffect(() => {
    setCurrentPage(0);
  }, [list.length]);

  const totalPages = Math.ceil(list.length / pageSize) || 1;
  const safeCurrentPage = Math.min(Math.max(0, currentPage), totalPages - 1);
  const startIdx = list.length === 0 ? 0 : safeCurrentPage * pageSize + 1;
  const endIdx = Math.min((safeCurrentPage + 1) * pageSize, list.length);
  const pagedItems = list.slice(safeCurrentPage * pageSize, safeCurrentPage * pageSize + pageSize);

  return (
    <section className="ia-section">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", flexWrap: "wrap", gap: "8px" }}>
        <h3 className="ia-subtitle" style={{ margin: 0 }}>
          {title} <span className="ia-count">{list.length}</span>
        </h3>
        {list.length > 0 && (
          <TablePagination
            pageSize={pageSize}
            totalRecords={list.length}
            onPageChange={({ currentPage: p }) => setCurrentPage(p)}
            currentPage={safeCurrentPage}
          />
        )}
      </div>
      {list.length === 0 ? (
        <div className="ia-card ia-empty">{empty}</div>
      ) : (
        <div className="ia-card ia-list">
          {pagedItems.map((a) => (
            <div className={`ia-act${urgent ? " ia-act--urgent" : ""}`} key={a.id}>
              <div className="ia-act__due">
                <strong>{a.due}</strong>
                {a.dueSub ? <small>{a.dueSub}</small> : null}
              </div>
              <div className="ia-act__body">
                <div className="ia-act__title">{a.title}</div>
                <div className="ia-act__why">{a.why}</div>
                <div className="ia-act__tags">
                  {a.tags.map((t) => (
                    <Tag key={t.label} level={t.level}>
                      {t.label}
                    </Tag>
                  ))}
                </div>
              </div>
              <div className="ia-act__btns">
                <button
                  type="button"
                  className={`ia-btn ${urgent ? "ia-btn--danger" : ""}`}
                  onClick={() => onOpen(a.target)}
                >
                  {OPEN_LABEL[a.target && a.target.view] || "Open"}
                </button>
              </div>
            </div>
          ))}

          {/* Pagination Footer */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "12px",
              padding: "10px 14px",
              borderTop: "1px solid var(--ia-line)",
              fontSize: "13px",
              color: "var(--ia-ink-2)",
              backgroundColor: "#fafbfc",
              borderBottomLeftRadius: "var(--ia-radius)",
              borderBottomRightRadius: "var(--ia-radius)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
              <span>
                Showing <strong>{startIdx}</strong> to <strong>{endIdx}</strong> of{" "}
                <strong>{list.length}</strong> actions
              </span>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", margin: 0, cursor: "pointer" }}>
                <span>Rows:</span>
                <select
                  className="ia-input ia-input--small"
                  style={{
                    padding: "2px 6px",
                    cursor: "pointer",
                    height: "28px",
                    width: "auto",
                    fontWeight: 600,
                  }}
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(0);
                  }}
                  aria-label="Rows per page"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </label>
            </div>

            <TablePagination
              pageSize={pageSize}
              totalRecords={list.length}
              onPageChange={({ currentPage: p }) => setCurrentPage(p)}
              currentPage={safeCurrentPage}
            />
          </div>
        </div>
      )}
    </section>
  );
}

export default function ActionCenter({ data, currency, onOpen }) {
  const k = data.kpis;
  const b = k.actTodayBreakdown || {};
  const parts = [
    b.stock && `${b.stock} stock`,
    b.inbound && `${b.inbound} inbound`,
    b.claims && `${b.claims} claim${b.claims > 1 ? "s" : ""}`,
    b.system && `${b.system} system`,
  ].filter(Boolean);
  return (
    <div>
      <p className="ia-lead">
        Everything that needs a person, sorted by deadline. Red means act today: money or sales are lost if it waits.
      </p>
      <KpiStrip
        items={[
          { label: "Act today", value: k.actToday, sub: parts.join(", ") || "Nothing urgent", level: k.actToday ? "crit" : "ok" },
          { label: "Due this week", value: k.dueThisWeek, sub: "Aging, fees, claims, inbound", level: k.dueThisWeek ? "warn" : null },
          { label: "At risk if missed", value: money(k.atRiskValue, currency), sub: "Lost sales + unclaimed value" },
          { label: "Closed automatically, 7 days", value: k.closedLast7Days, sub: null },
        ]}
      />
      <ActionList title="Act today" items={data.actToday} urgent onOpen={onOpen} empty="Nothing needs action today." />
      <ActionList title="Due this week" items={data.dueThisWeek} onOpen={onOpen} empty="Nothing due this week." />
    </div>
  );
}
